"use client";

/**
 * Floor plans of a residence's flats, from its plan archive (owner,
 * 2026-11-01: "preserve every kind of floor plan that we have found for
 * this specific building ... and filter to the ones that actually have a
 * listing right now"). The photo filters (#557, #560) find the plan among
 * a listing's photos; the backend keeps each plan after the listing left
 * its site, with our own copy of the image (services/residence_plans).
 *
 * One tile per layout with its room count, area and a believable price
 * (lib/residence-top planTiles). A plan for sale now is a real link to its
 * listing page (crawlers, a new tab on a modified click) and a plain tap
 * opens the listing in the drawer, like every card on the page. A plan no
 * listing shows any more says so, with the month it was last listed and
 * that listing's price, and opens large in the photo viewer.
 *
 * "Hamısı N / Satışda olanlar M" filters the tiles when there are both
 * kinds. Phones swipe a row, as the photo gallery does; from sm up four
 * tiles sit in a row, two rows until "all plans" is asked for. The plan
 * sits whole on white (object-contain): a cropped plan loses its rooms.
 */
import { useState } from "react";
import { ChevronDown } from "lucide-react";
import CardPhoto from "@/components/CardPhoto";
import Photo from "@/components/Photo";
import PhotoLightbox from "@/components/result/PhotoLightbox";
import { preloadDetailBody } from "@/components/listings/detail-body-loader";
import { mediaSrc, mediaThumb } from "@/lib/api";
import { useT } from "@/lib/locale-context";
import type { Locale } from "@/lib/i18n-config";
import { monthYear, planFullImage, type PlanTile } from "@/lib/residence-top";
import { listingPath } from "@/lib/seo/paths";
import { CHEVRON, LINK, SELECTED, TAG } from "@/lib/ui-classes";
import type { ListingCard } from "@/types";

const NBSP = String.fromCharCode(0xa0);
/** Tiles a computer shows before "all plans" (two rows of four). */
const DESKTOP_SHOWN = 8;
const TILE_SIZES = "(max-width: 639px) 62vw, 200px";
const TILE_CLASS = "group block h-full w-full overflow-hidden rounded-xl border border-gray-200 bg-white text-left transition-colors hover:border-gray-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2";
const PILL = "rounded-lg border px-2.5 py-1 text-xs font-semibold";
const PILL_OFF = "border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:text-gray-900 transition-colors";

function money(n: number, currency = "AZN"): string {
  return `${Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, NBSP)}${NBSP}${currency}`;
}

type Scope = "all" | "active";

export default function ResidencePlans({
  tiles, name, locale, onOpen,
}: {
  tiles: PlanTile<ListingCard>[];
  /** The residence name, for the plans' alt text. */
  name: string;
  locale: Locale;
  onOpen: (card: ListingCard) => void;
}) {
  const t = useT();
  const [scope, setScope] = useState<Scope>("all");
  const [more, setMore] = useState(false);
  const [viewer, setViewer] = useState<number | null>(null);
  // A plan whose picture fails (a site photo gone before we copied it)
  // leaves the row rather than showing an empty frame.
  const [broken, setBroken] = useState<ReadonlySet<string>>(new Set());

  const live = tiles.filter((x) => !broken.has(x.id));
  if (!live.length) return null;
  const forSale = live.filter((x) => x.card);
  const shown = scope === "active" && forSale.length ? forSale : live;
  const archived = shown.filter((x) => !x.card);
  const factsOf = (tile: PlanTile<ListingCard>) => [
    tile.rooms != null ? t("residence.rooms.label").replace("{n}", String(tile.rooms)) : null,
    tile.area != null ? `${tile.area}${NBSP}m²` : null,
  ].filter(Boolean).join(" · ");
  const markBroken = (id: string) => setBroken((prev) => new Set(prev).add(id));

  return (
    <>
      {forSale.length > 0 && forSale.length < live.length && (
        <div className="mt-3 flex flex-wrap items-center gap-1.5" role="group" aria-label={t("residence.plans.filter")}>
          {(["all", "active"] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => { setScope(s); setMore(false); }}
              aria-pressed={scope === s}
              className={`${PILL} ${scope === s ? SELECTED : PILL_OFF}`}
            >
              {t(s === "all" ? "residence.plans.scope.all" : "residence.plans.scope.active")}
              <span className="ml-1 tabular-nums">{s === "all" ? live.length : forSale.length}</span>
            </button>
          ))}
        </div>
      )}
      {/* scroll-px-4: a snapped tile keeps the page's gutter instead of
          sliding to the screen edge. */}
      <ul className="no-scrollbar -mx-4 mt-3 flex snap-x snap-mandatory scroll-px-4 gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:grid sm:grid-cols-4 sm:gap-3 sm:overflow-visible sm:px-0 sm:pb-0">
        {shown.map((tile, i) => {
          const facts = factsOf(tile);
          const alt = [t("residence.plans.alt").replace("{name}", name), facts].filter(Boolean).join(", ");
          const picture = (
            <span className="relative block aspect-[4/3] border-b border-gray-100 bg-[#fff]">
              {tile.hosted ? (
                <Photo
                  src={mediaThumb(tile.photo, 480) ?? tile.photo}
                  fallbackSrc={mediaSrc(tile.photo)}
                  alt={alt}
                  className="object-contain p-1"
                  onBroken={() => markBroken(tile.id)}
                />
              ) : (
                <CardPhoto
                  url={tile.photo}
                  sizes={TILE_SIZES}
                  alt={alt}
                  className="object-contain p-1"
                  onBroken={() => markBroken(tile.id)}
                />
              )}
            </span>
          );
          // A price that cannot be the flat's (a down payment) is left
          // off; the plan still shows.
          const price = tile.price != null && (
            <span className="mt-0.5 block text-sm tabular-nums text-gray-600">{money(tile.price, tile.currency)}</span>
          );
          const hide = i >= DESKTOP_SHOWN && !more ? "sm:hidden" : "";
          if (tile.card) {
            const card = tile.card;
            const path = listingPath(card.id);
            return (
              <li key={tile.id} className={`w-[62%] shrink-0 snap-start sm:w-auto ${hide}`}>
                <a
                  href={path ? `/${locale}${path}` : undefined}
                  onClick={(e) => {
                    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return;
                    e.preventDefault();
                    onOpen(card);
                  }}
                  onPointerEnter={preloadDetailBody}
                  onFocus={preloadDetailBody}
                  className={TILE_CLASS}
                >
                  {picture}
                  <span className="block px-3 py-2">
                    {facts && <span className="block truncate text-sm font-semibold text-gray-900 tabular-nums">{facts}</span>}
                    {price}
                  </span>
                </a>
              </li>
            );
          }
          const month = monthYear(tile.lastSeen, locale);
          const index = archived.indexOf(tile);
          return (
            <li key={tile.id} className={`w-[62%] shrink-0 snap-start sm:w-auto ${hide}`}>
              <button
                type="button"
                onClick={() => setViewer(index)}
                aria-label={t("residence.plans.open").replace("{plan}", alt)}
                className={`${TILE_CLASS} cursor-zoom-in`}
              >
                {picture}
                <span className="block px-3 py-2">
                  {facts && <span className="block truncate text-sm font-semibold text-gray-900 tabular-nums">{facts}</span>}
                  {price}
                  {month && (
                    <span className="mt-0.5 block text-xs text-gray-500">
                      {t(tile.price != null ? "residence.plans.last_price" : "residence.plans.last_seen").replace("{month}", month)}
                    </span>
                  )}
                  <span className={`mt-1.5 inline-flex rounded-md px-1.5 py-0.5 text-[11px] font-medium ${TAG}`}>
                    {t("residence.plans.archived")}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      {shown.length > DESKTOP_SHOWN && (
        <button
          type="button"
          onClick={() => setMore((v) => !v)}
          aria-expanded={more}
          className={`mt-3 hidden items-center gap-1 text-sm sm:inline-flex ${LINK}`}
        >
          {more ? t("residence.plans.fewer") : t("residence.plans.more").replace("{n}", String(shown.length))}
          <ChevronDown className={`h-4 w-4 ${CHEVRON} transition-transform duration-200 ${more ? "rotate-180" : ""}`} aria-hidden />
        </button>
      )}
      {/* The viewer portals itself to the body. */}
      {viewer != null && archived.length > 0 && (
        <PhotoLightbox
          photos={archived.map((tile) => ({
            url: tile.hosted ? mediaSrc(tile.photo) ?? tile.photo : planFullImage(tile),
            label: [factsOf(tile), t("residence.plans.archived")].filter(Boolean).join(" · "),
          }))}
          initialIndex={viewer}
          onClose={() => setViewer(null)}
        />
      )}
    </>
  );
}
